import { BookingInspectSave } from "../models/booking-model";
import { UtmData } from "../services/btr-booking.service";

export interface ApiResponse {
  success: boolean;
  errors: Error[];
  warning: Warning[];
}

export interface Error {
  code: string;
  description: string;
  recordRefID: string;
}

export interface Warning {
  code: string;
  description: string;
  recordRefID: string;
}

export interface CountryGroup {
  countryCode: string,
  countryName: string,
  items: CountryGroupItem[],
}

export interface CountryGroupItem {
  cultureCode: string,
  currencyCode: string,
  currencyDecimalDigits: number,
  currencyRoudingType: string,
  domainName: string,
  domainRefID: string,
  hotelName: string,
  hotelRefID: string,
  timeZoneCode: string,
  isOpenEndedContract?: boolean,
  totalMonthsOpenEndedContract: number,
  leaseStartFirstDateOfMonth?: number,
  fixedLeaseMonth?: number,
  minLeaseMonth?: number,
  alwayFullMonth?: number,
  countryCode?: string,
  numbersOfDaysAddedToToday?: number,
  rbeBillingSchedulesPattern ?: number,
  bookSiteTourURL?: string,
}

export interface HotelPhone {
  propertyCode: string,
  phoneNumber: string
}

export interface ReadCountryHotelGroupListResponse extends ApiResponse {
  countryGroups: CountryGroup[],
  hotelPhones: HotelPhone[],
}

export interface ReadNoOfBedResponse extends ApiResponse {
  items: BedroomOption[]
}

export interface ReadLeaseLengthResponse extends ApiResponse {
  items: LeaseOption[]
}

export interface BedroomOption {
  name: string,
  value: number,
  order: number
}

export interface LeaseOption {
  name: string,
  value: number,
  order: number
}

export interface SearchAvailbilityRQ {
  distributionChannelCode?: string,
  domainCode?: string,
  cultureCode?: string,
  platformCode?: string,
  action?: string,
  propertyCode: string,
  departureDate: Date,
  arrivalDate: Date,
  adultCount: number,
  promotionCodes: string[],
  numberOfBedrooms: number[],
  numberOfBathrooms: number[],
  selectedRooms?: SelectedRoomAvailability[],
  filterOption: FilterOptionSearchAvailability
}

export interface SelectedRoomAvailability {
  roomTypeCode: string,
  roomNumber: string,
  ratePlanCode: string
}

export interface FilterOptionSearchAvailability {
  isIgnoreMediaItemsAtt: boolean,
  isIgnoreRoomAmenitiesAtt: boolean,
  isIgnoreRoomTypeConfigsAtt: boolean,
  IsIgnoreRoomTypeRoomItemsAtt: boolean,
  pageIndex: number,
  roomTypeCode?: string,
  sectionCode?: string,
  furnishedFilterValues?: boolean[],
  priceRanges?: { priceFrom: number, priceTo: number }[]
  amenityFilterValues?: { amenityCode: string, category: string, amenityGroupCode: string }[]
}


export interface SearchAvailbilityRS extends ApiResponse {
  roomTypeRoomItems: RoomTypeRoomItem[],
  roomTypeConfigs: RoomTypeConfig[],
  hotelMediaItems: HotelMediaItem[],
  roomAmenities: RoomAmenity[],
  aspectCodes: string,
  furnishedCodes: string,
  isGroupRoomBySection: boolean,
  isValidPromoCode: boolean
}

export interface HotelMediaItem {
  refID: string,
  contentCategoryCode: string,
  contentReferenceCode: string,
  contentTitle: string,
  contentText: string,
  contentCopyrightNote: string,
  contentFilename: string,
  sortOrder: number,
}

export interface RoomTypeConfig {
  propertyCode: string,
  propertyName: string,
  domainCode: string,
  propertyTermAndCondition: string,
  derivedDailyRateTypeConfig: number,
  isOpenEndedContract: boolean,
  totalMonthsOpenEndedContract: number,
  propertyDescriptions: PropertyDescription[],
  mediaItems: MediaItem[],
  roomTypeAmenities: RoomTypeAmenity[],
  roomTypeItems: RoomTypeAmenity[]
  matterPortRoomTypes: MatterPortRoomType[],
}

export interface RoomTypeAmenity {
  shortDescription: string,
  longDescription: string,
  altName: string,
  altShortDescription: string,
  altLongDescription: string,
  roomTypeCode: string,
  roomTypeName: string,
  sortOrder: number,
  noOfBedRooms: number,
  noOfBathRooms: number,
  amenities: Amenity[],
  amenityViewGroups: AmenityGroup[],
}

export interface Amenity {
  code: string,
  name: string,
  amenityGroup: AmenityGroup
  mediaItems: MediaItem[]
}

export interface AmenityGroup {
  code: string,
  name: string,
  description: string,
  sortCode: number,
  groupCode: string,
  groupName: string,
  amenityCodes: string[]
}

export interface MediaItem {
  code: string,
  name: string,
  description: string,
  copyrightNotice: string,
  contentCode: string,
  categoryCode: string,
  referenceCode: string,
  sortCode: number,
  contentURL: string,
  contentFormatCode: string
}

export interface PropertyDescription {
  code: string,
  name: string,
  description: string,
  categoryCode: string,
  referenceCode: string,
  sortCode: number
}

export interface RoomTypeRoomItem {
  roomTypeCode: string,
  roomTypeName: string,
  inventoryAvalbilityCount: number,
  inventoryCount: number,
  numberOfBedrooms: number,
  numberOfBathrooms: number,
  roomTypeID: number,
  sortOrder: number,
  rooms: RoomTypeRoomItemRoom[],
  roomTypeAmenity?: RoomTypeAmenity,
  matterPortRoomType?: MatterPortRoomType,
  itemsToShow: number,
  isExpand: boolean
  sections: RoomSection[],
  amenities: Amenity[],
  expandAmenities: Boolean,
  totalPages: number,

  currentPageIndex: number,
  showExpand: boolean,
}

export interface RoomSection {
  sectionCode: string,
  sectionName: string,
  sectionDescription: string,
  sectionOrder: number,
  rooms: RoomTypeRoomItemRoom[],
  isExpand: boolean,
  showExpand: boolean,
  currentPageIndex: number,
  totalPages: number,
}

export interface RoomAmenity {
  roomTypeCode: string,
  roomTypeName: string,
  roomID: number,
  roomNumber: string,
  amenityCode: string,
  amenityName: string,
  amenityGroupCode: string,
  amenityGroupName: string,

  mediaItem?: HotelMediaItem,

}

export interface MatterPortRoomType{
  code?: string,
  name?: string,
  description?: string,
  categoryCode?: string,
  referenceCode?: string,
  sortCode?: number
}

export interface RoomTypeRoomItemRoom {
  hotelRoomID: number,
  roomNumber: string,
  floorName: string,
  rentalFloorArea: number,
  floorAreaUnit: string,
  ratePlans: RatePlan[],
  amenities: RoomAmenity[],
  furnishedItem?: RoomAmenity[],
  aspectItem?: RoomAmenity[],
  sectionCode: string,
  sectionName: string,
  sectionDescription: string,
  sectionOrder: number,
  maxOccupancy: number,
  floorPlanImage?: string,
  roomLongDescription: string,
  roomShortDescription: string,
  totalPages: number,

  // Extends
  isExpand?: boolean,
  minRatePlan: RatePlan,
  householdDetail?: string,
  images?: string[],
  heroImage?: string,
}

export interface RatePlan {
  roomRate: RoomRate,
  ratePlanCode: string,
  ratePlanName: string,
  ratePlanDescription: string,
  recordType: RecordType,
  rpmTransactionCodeID: number,
  domainRatePlanID: number,
  ratePlanID: number,
  marketCodeName: string,
  sourceCodeName: string,
  status: RatePlanStatus,
  promotionCode: string,
  currencyCode: string,
  ratePlanType: string,
  bookingRule: BookingRule
}

export interface RoomRate {
  hotelRoomID: number,
  roomNumber: string,
  date: string,
  rateType: RateType,
  amount: number
}

export interface BookingRule {
  guaranteePolicy: GuaranteePolicy,
  cancelPolicy: CancelPolicy
}

export interface CancelPolicy {
  code: string,
  name: string,
  description: string,
  cancelNoticeNumberDays: number,
  cancelNoticeNumberWeeks: number,
  cancelNoticeNumberMonths: number,
  recordType: RecordType
}

export type RateType = 'Monthly' | 'Weekly';
export type RecordType = 'LongStay' | 'ShortStay';
export type RatePlanStatus = 'Available';

export interface SaveDraftApplicationRQ {
  preApplicationChecklist: PreApplicationChecklist,
  profileApplication: ProfileApplication,
  propertyCode: string,
  applicantTypeCode: string,
  distributionChannelCode: string,
  applicationID?: string,
  cultureCode: string,
  domainCode: string,
  platformCode: string,
  action: string,
  isForceContinue: boolean,
  extras: ExtrasConfigurationModel[],
  promoCodes?: string[],
  hotelBookingDiscounts?: DiscountItem[],
  utmInfo?: UtmData | null,
}

export interface SaveDraftApplicationRS extends ApiResponse {
  applicationID: string,
  redirectCode: string,
}

export interface saveBookingInspectionRQ {
  bookInspectionModel: BookingInspectSave
}

export interface ReadApplicationPaymentSummaryRequest {
  propertyCode: string,
  roomTypeCode: string,
  ratePlanCode: string,
  roomNumber: string,
  arrivalDate: Date,
  departureDate: Date,
  numberOfBathrooms: number,
  numberOfBedrooms: number,
  adultCount: number,
  extras?: ExtrasConfigurationModel[],
  promotionCode?: string
}

export interface ReadApplicationPaymentSummaryRQ {
  distributionChannelCode: string,
  domainCode: string,
  cultureCode: string,
  platformCode: string,
  request: ReadApplicationPaymentSummaryRequest
}

export interface RateMonthlyPayment {
  rateAmount: number,
  rateType: string,
  rateForPeriod: number,
  supplementary: number,
  totalAmount: number,
  monthlyRateAmount: number
}

export interface UtilitiesPayment {
  unitPrice: number,
  rateForPeriod: number,
  supplementary: number,
  totalAmount: number,
  extraName: string,
  extraCode: string,
  recurrencePattern: string,
  quantity: number,
  monthlyRateAmount: number,
  taxFee: ExtraTaxFee
}

export interface ExtraTaxFee {
  extraCode: string,
  rpmTransactionCodeID: number,
  incTaxFeeItems: TaxFeeItem[],
  excTaxFeeItems: TaxFeeItem[]
}

export interface TaxFeeItem {
  refID:	string,
  name:	string,
  description:	string,
  taxFeeType:	number,
  inclusionType:	number,
  percent:	number,
  amount:	number
}

export interface PaymentSummary {
  adminFeeAmount: number,
  bondAmount: number,
  actualBondAmount: number,
  securityAmount: number,
  rateAmountOfFirstMonth: number,
  hodingFeeAmount: number,
  totalUtilitiesAmountOfFirstMonth: number,
  currency: string,
  totalMonthsDeposit: number,
  firstMonthRent: number,
  totalAddonsAmountOfTheFirstMonth: number,
  advanceRentAmountOfFirstMonth: number,
  initialPaymentAmountOfFirstMonth: number,
  rate: RateMonthlyPayment,
  utilities: UtilitiesPayment[],
  addOns: any[],
  arrivalDate: string,
  departureDate: string,
  guaranteePolicy: GuaranteePolicy
}

export interface GuaranteePolicy {
  code: string,
  name: string,
  description: string,
  depositNumberWeeks: number,
  depositNumberMonths: number,
  adminFeeAmount: number,
  recordType: RecordType,
  holdingFeeNumberWeeks: number,
  holdingFeeNumberMonths: number,
  advanceRentNumberWeeks: number,
  advanceRentNumberMonths: number,
  bondAmountNumberWeeks: number,
  bondAmountNumberMonths: number,
  merchantCollectBondOnBehalf: boolean,
  bondAmount: number,
}

export interface ReadApplicationPaymentSummaryRS {
  paymentSummary: PaymentSummary
  success: boolean
}

export interface PreApplicationChecklist {
  id?: number,
  applicantTypeCode?: string,
  profileApplicationID?: number,
  numBathrooms: number,
  numBedrooms: number,
  whoMovingIn: number,
  movingInDate: Date,
  isHasPet: number,
  needParking: number,
  needExtraStorage: number
}

export interface ProfileApplication {
  id?: number,
  applicationID?: string,
  hotelID?: number,
  applicantTypeID?: number,
  hotelRoomID: number,
  roomTypeCode: string,
  roomTypeID: number,
  roomTypeName: string,
  roomNumber: string,
  ratePlanName: string,
  ratePlanCode: string,
  status?: number,
  arrivalDate: Date,
  departureDate: Date,
  bookingNumber?: string,
}

export interface ReadApplicationConfigurationsRS extends ApiResponse {
  applicantTypes: ApplicantType[]
}

export interface ReadBTRHotelRoomAmenitiesRS extends ApiResponse {
  items: RoomAmenity[], 
  configItems: RoomAmenity[],
  hotelMediaItems: HotelMediaItem[],
}

export interface ReadBTRAmenitiesSearchableRS extends ApiResponse {
  groupItems: AmenityGroupItem[],
}

export interface AmenityGroupItem {
  amenityGroupCode: string,
  amenityGroupName: string,
  amenityGroupSortOrder: number,
  items: AmenityItem[],
}

export interface AmenityItem {
  amenityCode: string,
  amenityName: string,
  amenitySortOrder: number,
  isViewable: boolean,
  isSearchable: boolean,
  category: string,
  amenityGroupCode?: string,
}
export interface ApplicantType {
  id: number
  name: string
  code: string
  description: string
  hotelID: number
  recordStatus: number
}

export interface RegisterRQ {
  profile: RegisterProfileRQ
  application?: SaveDraftApplicationRQ
}

export interface RegisterProfileRQ {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  password?: string;
  phoneNumber?: string;
  phoneExt?: string;
  hotelBookingID?: string;
  rootDomainCode?: string;
  logonName?: string;
  cultureCode?: string;
  sortCode?: string;
  sourceCode?: string;
  verifyUserAccountLink?: string;
  locale?: string
}

export interface ReadAllApplicationForProfileRS extends ApiResponse {
  applicationForProfileModels: ProfileApplication[]
}

export interface ReadRentalItemTypeRateAvailabilityRQ {
  distributionChannelCodes: string[],
  hotelCode: string,
  rentalItemTypeCodes: string[],
  arrivalDate: Date,
  departureDate: Date,
  leaseStartDate?: Date,
  leaseEndDate?: Date,
  timeSlotActivityTypes: string[],
  timeSlotActivityItemTypes: string[],
  roomTypeCodes: string[],
  bookingConditionPersonTypes: string[]
  isGetSystemStimeSlotOnly: boolean,
  filterOption?: RentalItemFilterOption
}

export interface RentalItemFilterOption {
  isIgnoreRentalItemTypesForNonResidentAtt: boolean,
  isIgnoreRoomTypeItemsAtt: boolean,
  isIgnoreSystemTimeSlotsAtt: boolean
}

export interface ReadRentalItemTypeRateAvailabilityRS extends ApiResponse {
  bookingConditions: any,
  roomTypeItems: RoomTypeItem[],
  rentalItemTypesForNonResident: any,
  systemTimeSlots: TimeSlotConfigure[]
}

export interface ReadSearchDiscountByAccessCodeDistributionChannelCodeRQ {
  accessCode: string[],
  propertyCode: string,
  distributionChannelCode: string,
  stayFromDate: Date,
  stayToDate: Date
}

export interface ReadSearchDiscountByAccessCodeDistributionChannelCodeRS{
  collection: Discount[],
  success: boolean
}

export interface Discount{
  accessCode: string
  approvalCode: string
  bookingFromDate: string
  bookingToDate: string
  code: string
  description: string
  items: DiscountItem[]
  id: number
  isLongStayUse: boolean
  isShortStayUse: boolean
  isStaffUseOnly: boolean
  name: string
  recordStatus: boolean
  stayFromDate: string
  stayToDate: string
}

export interface DiscountItem{
  amount: number
  appliedRPMTransactionCodeID: number
  createDateTime?: string
  discountRPMTransactionCodeID: number
  id?: number
  percent: number
  rpmDiscountID: number
  updateDateTime?: string
  stayFromDate?: Date
  stayToDate?: Date
}

export interface RoomTypeItem {
  rentalItemTypes: RentalItemType[],
  roomTypeCode: string,
  roomTypeID: number,
  roomTypeName: string
}

export interface RentalItemType {
  maxQtyPerBooking: number,
  minQtyPerBooking: number,
  rentalItemTypeCode: string,
  rentalItemTypeID: string,
  rentalItemTypeLongDescription: string,
  rentalItemTypeName: string,
  rentalItemTypeGroupCode?: string,
  rentalItemTypeSortOrder?: number,
  rentalItemTypeShortDescription: string,
  invenrotyItems: InventoryItem[],
  extraName: string,
  extraCode: string,
  isValid: boolean,
  totalInventory: number,
  totalAvail: number,
  extraRecurrencePattern: string
  // user select
  selectQuantity?: number
}

export interface InventoryItem {
  fromDate: Date,
  toDate: Date,
  totalInventory: number,
  totalBooked: number,
  totalAvail: number,
  baseRateAmount: number,
  overrideRate: number,
  rateAmount: number,
  isValid: boolean
}

export interface TimeSlotConfigure {
  // id: number,
  systemTimeSlotID: string,
  code: string,
  name: string,
  shortName: string,
  description: string,
  activityItemType: string,
  systemTypeID: number,
  systemTypeCode: string,
  systemTypeName: string,
  systemTypeDescription: string,
  dates: TimeSlotDates[]
}

export interface TimeSlotDates {
  date: Date,
  isOpenToBook: boolean,
  isAvailable: boolean,
  times: TimeSlot[]
}

export interface TimeSlot {
  rentalItemBookingItemID	: string,
  allotmentID: string,
  allotmentItemGenerateID?: number,
  allotmentItemGenerateIDs: number[],
  timeSlotItemID: number,
  fromTime: string,
  toTime: string,
  fromHour: number,
  fromMinute: number,
  toHour: number,
  toMinute: number,
  fromTimeDisplay: string,
  toTimeDisplay: string,
  totalInventory: number,
  totalBooked: number,
  totalAvail: number,
  isAvailable: boolean,
  description: string
}

export interface ReadProfileApplicationRQ {
  applicationID: string | number,
  distributionChannelCode: string,
  domainCode: string,
  cultureCode: string,
  platformCode: string,
  action: string,
}

export interface BtrReadProfileApplicationDetailResponse {
  applicationSteps: ApplyApplicationStep[],
  preApplicationChecklist: PreApplicationChecklist,
  application: ApplicationSummary,
  paymentSummary: PaymentSummary,
  success: boolean
}

export interface ApplyApplicationStep {
  id?: number
  key?: string,
  label?: string,
  link?: string,
  params?: any,
  index?: number
  success?: number,
  profileApplicationID?: number,
  name?: string,
  code?: string,
  componentType?: number,
  recordStatus?: number,
  stepStatus?: string,
  isRequired?: boolean,
  required?: string,
  score?: number,
  passToStepID?: number,
  passToStepCode?: string,
  passToStepApplStatus?: string
  declineToStepID?: number,
  declineToStepCode?: string,
  declineToStepApplStatus?: string
  hidden?: boolean,
  failed?: boolean
}

export interface ApplicationSummary {
  applicationID?: string,
  bookingNumber?: string,
  hotelRefID?: string,
  hotelName?: string,
  bookingStatus?: number
  resStatusType?: number,
  leaseID?: number,
  profileRentalRequirementID?: string,
  roomTypeName?: string,
  roomTypeCode?: string,
  ratePlanName?: string,
  ratePlanCode?: string,
  hotelBookingID?: string,
  roomNumber?: string,
  arrivalDate: string | Date,
  departureDate?: string | Date,
  advancedRentReceived?: number,
  bondAmountReceived?: number,
  adminFeeAmountReceived?: number,
  isTenantSigned?: boolean,
  envelopeRefID?: string,
  leaseLength?: number
}

export interface BtrReadProfileResponse {
  profileContactDetail: BtrProfileAndContactDetail,
  success: boolean
}

export interface BtrProfileAndContactDetail {
  firstName?: string,
  middleName?: string,
  lastName?: string,
  gender?: string,
  title?: string,
  dateOfBirth?: Date,
  countryNationality?: string,
  countryResidency?: string,
  mobile?: string,
  phoneMobileExt?: string,
  phoneHome?: string,
  phoneWork?: string,
  emailAddressHome?: string,
  emailAddressWork?: string,
  addressLine1?: string,
  addressLine2?: string,
  addressLine3?: string,
  city?: string,
  postcode?: string,
  countryCode?: string,
  countryName?: string,
  stateCode?: string,
  stateName?: string
}

export interface ReadAddsOnListRQ {
  propertyCode: string,
  domainCode: string,
  distributionChannelCode: string,
  cultureCode: string,
  roomTypeCode: string,
  ratePlanCode: string,
  arrivalDate: string | Date,
  departureDate: string | Date,
  roomNumber: string,
  numBathrooms: number[],
  numBedrooms: number[],
  promotionCode?: string,
  extraType: "All" | "Utilities" | "AddsOn"
}

export interface ReadAddsOnListRS {
  rbeExtraItems: ExtrasConfigurationModel[]
  hotelMediaItems?: HotelMediaItem[]
  rentalItemInvCheckMode: 0 | 1,
  description: string,
  success: boolean
}

export interface ExtrasConfigurationModel {
  ratePlanCode: string
  extraCode: string
  extraAccessCode: string
  extraName: string
  extraDescription: string
  priceType: string
  price: number
  quantityIncludedInRatePlan: number
  defaultPurchaseQuantity: number
  permitAdditionalPurchase: boolean
  extraRateExclusiveToRent: number
  bookablePackage: number
  status: string
  rpmTransactionCodeID: string
  hotelRatePlanExtraID: number
  domainRatePlanExtraID: number
  recurrencePattern: string
  includedInRoomRate: number
  quantity: number
  rentalItemTypeID: string
  rentalItemType?: RentalItemType,
  images?: HotelMediaItem[]
}

export interface ReadSystemTypesByActivityTypeRQ {
  distributionChannelCode: string,
  propertyCode: string,
  activityItemType: string
}

export interface ReadSystemTypesByActivityTypeRS {
  systemTypesByActivityTypeModels: systemTypesByActivityTypeModel[],
  success: boolean
}

export interface systemTypesByActivityTypeModel {
  id: number,
  hotelID: number,
  code: string,
  name: string,
  shortName: string,
  sortOrder: number,
  effectiveFrom: Date | string,
  effectiveTo: Date | string,
  instructionOnly: boolean,
  remark: string,
  activityType: number,
  description: string,
}

export interface ApiGetInProgressApplicationsRS extends ApiResponse{
  applicationID: string,
  redirectCode: string,
  resStatusType: string,
  bookingNumber: string,
}

export enum HotelBookingResStatusType {
  Confirmed = 0,
  OptionalWithReductionToInventory = 1,
  Cancelled = 2,
  NoShow = 3,
  OptionalWithoutReductionToInventory = 4,
  WaitingList = 5,
  Block = 6,
  Incomplete = 7,
  CheckoutClosedAccount = 8,
  CheckoutTransactionPending = 9
}

export enum PropertyDescriptionCode{
  ShowTextUnderRefineYourSearch = 'ShowTextUnderRefineYourSearch',
  PrivacyResidentialText = 'PrivacyResidentialText',
  PrivacyEmploymentText = 'PrivacyEmploymentText'
};

export interface ApiReadHotelContactInformationRS extends ApiResponse{
  emailAddress: string,
  phoneNumber: string,
  phoneNumberExt: string,
  businessEmailAddress: string,
  businessPhoneNumber: string,
  businessPhoneNumberExt: string
}

export interface HotelContactInformation{
  phone: string,
  phoneDisplay: string,
  email: string,
  businessEmail?: string,
}